import s from "../../../wizard.module.css";
import {
  Section,
  PersonaCard,
  Pill,
  ChipDato,
  EmptyState,
} from "../../../FieldHelpers";
import { nombreDe, metaDe, placasDe, inicialesDe } from "../../../persona";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../../../shared/i18n/useLang";
import { calcularCambios } from "./cambios";

const COLOR_CAMBIO = {
  agregado: { tono: "ok", icon: "fa-plus", label: "Agregado" },
  quitado: { tono: "danger", icon: "fa-minus", label: "Quitado" },
  modificado: { tono: "brand", icon: "fa-pen", label: "Cambio" },
};

// Botón "Editar" que devuelve al paso correspondiente
function BtnEditar({ onClick, label }) {
  if (!onClick) return null;
  return (
    <button type="button" className={`${s.btnGhost} ${s.btnSmall}`} onClick={onClick}>
      <i className="fa-solid fa-pen" />
      {label}
    </button>
  );
}

function Tile({ label, value, grande = false }) {
  return (
    <div className={s.tile}>
      <span className={s.tileLabel}>{label}</span>
      <span
        className={[s.tileValue, grande ? s.tileValueBig : ""].filter(Boolean).join(" ")}
        title={value || "-"}
      >
        {value || "-"}
      </span>
    </div>
  );
}

function ContactTile({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className={s.contactTile}>
      <span className={s.contactLabel}>
        <i className={`fa-solid ${icon}`} />
        {label}
      </span>
      <span className={s.contactValue} title={value}>
        {value}
      </span>
    </div>
  );
}

// ── Resumen de Editar: solo lo que cambió respecto a lo guardado ────────────
function ResumenCambios({ original, formData }) {
  const cambios = calcularCambios(original, formData);
  const secciones = [...new Set(cambios.map((c) => c.seccion))];

  return (
    <Section
      title={`${cambios.length} cambio(s) en la filial ${formData.filial.idFilial}`}
      subtitle="Al guardar se aplican todos estos cambios de una sola vez."
    >
      {cambios.length === 0 ? (
        <EmptyState>No hiciste ningún cambio. Podés volver atrás para editar algo.</EmptyState>
      ) : (
        <div className={s.changeList}>
          {secciones.map((sec) => (
            <div key={sec} className={s.changeGroup}>
              <div className={s.changeGroupTitle}>{sec}</div>
              {cambios
                .filter((c) => c.seccion === sec)
                .map((c, i) => {
                  const cfg = COLOR_CAMBIO[c.tipo] || COLOR_CAMBIO.modificado;
                  return (
                    <div key={i} className={s.changeRow}>
                      <Pill tono={cfg.tono}>
                        <i className={`fa-solid ${cfg.icon}`} />
                        {cfg.label}
                      </Pill>
                      <span>
                        {c.texto}
                        {c.tipo === "modificado" && (
                          <span className={s.changeDiff}>
                            <span className={s.changeBefore}>{c.antes}</span>
                            {"  →  "}
                            <span className={s.changeAfter}>{c.despues}</span>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export default function Confirmation({ formData, original, irAPaso }) {
  const esCliente = useEsCliente();
  const L = useLang();

  if (original) return <ResumenCambios original={original} formData={formData} />;

  const p = formData.propietario;
  const nombre = nombreDe(p);
  const placas = placasDe(p);
  const condominio = localStorage.getItem("condominio_nombre") || "";
  const correo = p.sin_correo ? L("No tiene correo", "No email") : p.emailPrincipal;

  // ── Modo cliente ──────────────────────────────────────────────────────────
  if (esCliente) {
    return (
      <>
        <Section
          title={L("Datos del cliente", "Client details")}
          action={<BtnEditar onClick={() => irAPaso?.(0)} label={L("Editar", "Edit")} />}
        >
          <div className={s.tileGrid}>
            <Tile label={L("ID / Código", "ID / Code")} value={formData.filial.idFilial} grande />
            <Tile label={L("Identificación", "ID number")} value={p.cedula} />
            <Tile label={L("Tipo", "Type")} value={p.tipo_cedula} />
            <Tile label={L("Provincia", "Province")} value={p.direccion_provincia} />
          </div>
        </Section>

        <Section
          title={L("Contacto", "Contact")}
          action={<BtnEditar onClick={() => irAPaso?.(1)} label={L("Editar", "Edit")} />}
        >
          <div className={s.ownerCard}>
            <div className={s.ownerHead}>
              <div className={s.ownerIdent}>
                <span className={`${s.avatar} ${s.avatarBig}`}>{inicialesDe(p)}</span>
                <div className={s.personInfo}>
                  <div className={s.ownerName}>{nombre}</div>
                  <div className={s.personMeta}>{p.cedula}</div>
                </div>
              </div>
            </div>
            <div className={s.contactGrid}>
              <ContactTile icon="fa-envelope" label={L("Correo", "Email")} value={correo} />
              <ContactTile
                icon="fa-mobile-screen"
                label={L("Celular", "Mobile")}
                value={p.telefono_cel}
              />
              <ContactTile
                icon="fa-phone"
                label={L("Otro", "Other")}
                value={p.telefono_trabajo || p.telefono_casa}
              />
            </div>
          </div>
        </Section>

        <div className={s.notice}>
          <i className={`fa-solid fa-circle-info ${s.noticeIcon}`} />
          <span>
            {L(
              "Al confirmar se creará el cliente con todos sus datos.",
              "Confirming will create the client with all its data.",
            )}
          </span>
        </div>
      </>
    );
  }

  // ── Modo condominio ───────────────────────────────────────────────────────
  return (
    <>
      <Section
        title="Datos de la Filial"
        action={<BtnEditar onClick={() => irAPaso?.(0)} label="Editar" />}
      >
        <div className={s.tileGrid}>
          <Tile label="ID Filial" value={formData.filial.idFilial} grande />
          <Tile label="Plano catastrado" value={formData.filial.numPlano} />
          <Tile label="Folio real" value={formData.filial.numFolio} />
          <Tile label="Propiedad" value={formData.filial.tipoPropiedad} />
        </div>
      </Section>

      <Section
        title="Propietario principal y copropietarios"
        action={<BtnEditar onClick={() => irAPaso?.(1)} label="Editar" />}
      >
        <div className={s.ownerGrid}>
          <div className={s.ownerCard}>
            <div className={s.ownerHead}>
              <div className={s.ownerIdent}>
                <span className={`${s.avatar} ${s.avatarBig}`}>{inicialesDe(p)}</span>
                <div className={s.personInfo}>
                  <div className={s.ownerName}>
                    <span>{nombre}</span>
                    <Pill tono="ok">Titular</Pill>
                  </div>
                  <div className={s.personMeta}>Cédula: {p.cedula || "-"}</div>
                </div>
              </div>
              {placas.length > 0 && (
                <div className={s.ownerPlates}>
                  {placas.map((placa) => (
                    <ChipDato key={placa} icon="fa-car">
                      {placa}
                    </ChipDato>
                  ))}
                </div>
              )}
            </div>

            <div className={s.contactGrid}>
              <ContactTile icon="fa-envelope" label="Correo" value={correo} />
              <ContactTile icon="fa-mobile-screen" label="Celular" value={p.telefono_cel} />
              <ContactTile
                icon="fa-phone"
                label="Casa"
                value={p.telefono_casa || p.telefono_trabajo}
              />
            </div>
          </div>

          <div className={s.sideCard}>
            <div className={s.sideLabel}>Copropietarios ({formData.copropietarios.length})</div>
            {formData.copropietarios.length === 0 ? (
              <EmptyState>Sin copropietarios.</EmptyState>
            ) : (
              <>
                <div className={s.personList}>
                  {formData.copropietarios.map((co) => (
                    <PersonaCard
                      key={co.id}
                      anidada
                      nombre={nombreDe(co)}
                      avatar={inicialesDe(co)}
                      meta={metaDe(co)}
                    />
                  ))}
                </div>
                <p className={s.sideNote}>
                  Los copropietarios quedan con derecho sobre la filial y acceso autorizado.
                </p>
              </>
            )}
          </div>
        </div>
      </Section>

      <Section
        title={`Autorizados registrados (${formData.autorizados.length})`}
        action={<BtnEditar onClick={() => irAPaso?.(2)} label="Editar" />}
      >
        {formData.autorizados.length === 0 ? (
          <EmptyState>Sin personas autorizadas.</EmptyState>
        ) : (
          <div className={s.personList}>
            {formData.autorizados.map((auth) => {
              const sus = placasDe(auth);
              return (
                <PersonaCard
                  key={auth.id}
                  nombre={nombreDe(auth)}
                  avatar={inicialesDe(auth)}
                  pill={<Pill tono="brand">{auth.relacion}</Pill>}
                  meta={`ID: ${auth.cedula || "-"}`}
                  chips={
                    sus.length ? (
                      <ChipDato icon="fa-car">
                        {sus.join(", ")}
                      </ChipDato>
                    ) : null
                  }
                />
              );
            })}
          </div>
        )}
      </Section>

      <div className={s.notice}>
        <i className={`fa-solid fa-circle-info ${s.noticeIcon}`} />
        <span>
          Al confirmar se guardará el registro permanentemente
          {condominio ? (
            <>
              {" en "}
              <strong className={s.strongName}>{condominio}</strong>
            </>
          ) : (
            " en el condominio activo"
          )}
          .
        </span>
      </div>
    </>
  );
}
